import { useMemo, useState, type FormEvent } from 'react'
import { Check, Hash, Search } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { channelName, displayName, useSocialStore } from '@/store/social'
import { describeError, publicUser } from '@/lib/auth'
import { avatarUrl, guildIconUrl } from '@/lib/avatar'
import { compareIds } from '@/lib/messageMerge'
import { P, channelPermissions, has } from '@/lib/permissions'
import { cx } from '@/lib/cx'
import { plural } from '@/lib/guildUi'
import { ChannelType, MessageFlags, type AvatarDecoration, type Member, type Message, type User } from '@/types/api'
import { Modal } from '../settings/Modal'
import { Avatar } from '../Avatar'
import { GroupAvatar } from '../GroupAvatar'
import styles from './ForwardModal.module.css'

const MAX_TARGETS = 5
const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

interface Target {
  channelId: string
  name: string
  sub?: string | undefined
  kind: 'dm' | 'group' | 'text'
  image?: string | undefined
  /** Рамка аватара собеседника — у бесед один на один. */
  decoration?: AvatarDecoration | null | undefined
  /** Собеседники группы — для двух аватарок внахлёст. */
  recipients?: User[] | undefined
  imageId: string
  /** Для сортировки: последнее сообщение — недавние беседы выше. */
  last: string
}

/** Куда можно переслать: беседы и группы, текстовые каналы серверов, где можно писать (и прикреплять, если есть файлы). */
function useTargets(need: bigint | null): Target[] {
  const me = useAuthStore((s) => s.user)
  const dms = useSocialStore((s) => s.channels)
  const guilds = useGuildStore((s) => s.guilds)
  const channels = useGuildStore((s) => s.channels)
  const members = useGuildStore((s) => s.members)
  return useMemo(() => {
    const out: Target[] = []
    for (const ch of Object.values(dms)) {
      if (ch.type === ChannelType.DM) {
        const r = ch.recipients?.[0]
        out.push({ channelId: ch.id, name: channelName(ch), sub: r ? `@${r.username}` : undefined, kind: 'dm', image: r ? avatarUrl(r.id, r.avatar) : undefined, decoration: r?.avatar_decoration_data, imageId: r?.id ?? ch.id, last: ch.last_message_id ?? '0' })
      } else if (ch.type === ChannelType.GroupDM) {
        out.push({ channelId: ch.id, name: channelName(ch), sub: 'Группа', kind: 'group', recipients: ch.recipients, imageId: ch.id, last: ch.last_message_id ?? '0' })
      }
    }
    if (me) {
      for (const g of Object.values(guilds)) {
        const self: Member = members[g.id]?.[me.id] ?? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null }
        for (const ch of Object.values(channels[g.id] ?? {})) {
          if (ch.type !== ChannelType.GuildText) continue
          const perms = channelPermissions(g, self, ch)
          if (!has(perms, P.VIEW_CHANNEL) || !has(perms, P.SEND_MESSAGES) || (need !== null && !has(perms, need))) continue
          out.push({ channelId: ch.id, name: ch.name ?? '', sub: g.name, kind: 'text', image: guildIconUrl(g.id, g.icon), imageId: g.id, last: ch.last_message_id ?? '0' })
        }
      }
    }
    return out.sort((a, b) => compareIds(b.last, a.last))
  }, [me, dms, guilds, channels, members, need])
}

/** Переслать сообщение: до пяти бесед и каналов сразу, подпись уходит следом отдельным сообщением. */
export function ForwardModal({ message, onClose }: { message: Message; onClose: () => void }) {
  const forward = useSocialStore((s) => s.forwardMessage)
  const sendMessage = useSocialStore((s) => s.sendMessage)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  // что пересылаем: своё содержимое или копия, если это уже пересланное
  const snapshot = message.message_snapshots?.[0]?.message
  const preview = (snapshot?.content ?? message.content).trim()
  const files = (snapshot?.attachments ?? message.attachments ?? []).length
  const voice = ((snapshot?.flags ?? message.flags ?? 0) & MessageFlags.IsVoiceMessage) !== 0
  // файлы — туда, где их можно прикреплять, голосовое — где можно голосовые
  const targets = useTargets(files ? (voice ? P.SEND_VOICE_MESSAGES : P.ATTACH_FILES) : null)

  const q = query.trim().toLocaleLowerCase('ru')
  const shown = q ? targets.filter((t) => `${t.name} ${t.sub ?? ''}`.toLocaleLowerCase('ru').includes(q)) : targets
  const toggle = (id: string) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX_TARGETS ? cur : [...cur, id]))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (picked.length === 0 || busy) return
    setBusy(true)
    setError(null)
    const text = comment.trim()
    const failed: string[] = []
    const noComment: string[] = []
    let first: unknown = null
    for (const id of picked) {
      try {
        await forward(id, message)
      } catch (err) {
        failed.push(id)
        first ??= err
        continue
      }
      // подпись — отдельным сообщением; не ушла (например, медленный режим) — пересылку не повторяем,
      // сообщение остаётся в канале с кнопкой повтора
      if (text) {
        try {
          await sendMessage(id, text)
        } catch (err) {
          noComment.push(id)
          first ??= err
        }
      }
    }
    setBusy(false)
    if (failed.length === 0 && noComment.length === 0) {
      setDone(true)
      setTimeout(onClose, 700)
      return
    }
    // в списке остаются только те, куда не переслано: можно повторить
    setPicked(failed)
    if (failed.length === 0) setDone(true)
    const names = (ids: string[]) => ids.map((id) => targets.find((t) => t.channelId === id)?.name ?? id).join(', ')
    const parts = [failed.length ? `Не переслано: ${names(failed)}.` : '', noComment.length ? `Подпись не отправлена: ${names(noComment)}.` : '']
    setError(`${parts.filter(Boolean).join(' ')} ${describeError(first).message}`)
  }

  return (
    <Modal title="Переслать" onClose={onClose} width={480}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <blockquote className={styles.preview}>
          <strong>{displayName(message.author)}</strong>
          <span className={styles.previewText}>{preview || (voice ? '🎤 Голосовое сообщение' : files ? `📎 ${files} ${plural(files, 'файл', 'файла', 'файлов')}` : 'Сообщение')}</span>
        </blockquote>
        <label className={styles.search}>
          <Search size={16} className={styles.searchIcon} aria-hidden="true" />
          <input value={query} autoFocus placeholder="Найти беседу или канал" aria-label="Найти беседу или канал" onChange={(e) => setQuery(e.target.value)} />
        </label>
        <ul className={styles.list} aria-label="Куда переслать">
          {shown.length === 0 ? <li className={styles.empty}>Ничего не найдено</li> : null}
          {shown.map((t) => {
            const on = picked.includes(t.channelId)
            const full = !on && picked.length >= MAX_TARGETS
            return (
              <li key={t.channelId}>
                <button type="button" className={cx(styles.row, on && styles.rowOn)} role="checkbox" aria-checked={on} disabled={full || busy} onClick={() => toggle(t.channelId)}>
                  {t.kind === 'text' ? (
                    <span className={styles.channelIcon}><Hash size={18} /></span>
                  ) : t.kind === 'group' ? (
                    <GroupAvatar recipients={t.recipients} />
                  ) : (
                    <Avatar initial={t.name.slice(0, 1).toUpperCase()} color={colorOf(t.imageId)} size={32} src={t.image} decoration={t.decoration} />
                  )}
                  <span className={styles.names}>
                    <span className={styles.name}>{t.name}</span>
                    {t.sub ? <span className={styles.sub}>{t.sub}</span> : null}
                  </span>
                  <span className={cx(styles.box, on && styles.boxOn)} aria-hidden="true">{on ? <Check size={12} strokeWidth={3} /> : null}</span>
                </button>
              </li>
            )
          })}
        </ul>
        <input className={styles.comment} value={comment} maxLength={2000} placeholder="Добавить сообщение (необязательно)" aria-label="Сообщение к пересылке" onChange={(e) => setComment(e.target.value)} />
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div className={styles.buttons}>
          <span className={styles.count}>{picked.length > 0 ? `Выбрано ${picked.length} из ${MAX_TARGETS}` : `До ${MAX_TARGETS} получателей`}</span>
          <button type="button" className={styles.cancel} onClick={onClose}>{done ? 'Закрыть' : 'Отмена'}</button>
          <button type="submit" className={styles.primary} disabled={picked.length === 0 || busy || done}>{done ? 'Переслано' : busy ? 'Пересылаем…' : 'Переслать'}</button>
        </div>
      </form>
    </Modal>
  )
}
